/// <reference types="vite/client" />
import * as React from 'react';
import { useModalStore, type ModalInstance, type ModalRegistryMap } from '@noor/lib';
import { ModalControlsProvider } from './modal-context';
import { ModalErrorBoundary } from './modal-error-boundary';

interface ModalHostProps {
  registry: ModalRegistryMap;
}

/**
 * Renders the open modal stack from the Zustand store. Mounted once near the
 * app root. For each instance it:
 *   1. looks up the registry definition for the id,
 *   2. validates the payload against the definition's Zod schema (if any),
 *   3. renders the component inside a per-modal error boundary,
 * passing the validated payload as a typed prop.
 *
 * A bad payload or a thrown render closes that one instance — it never blanks
 * the app. The host knows the registry shape but nothing about modal contents.
 */
export function ModalHost({ registry }: ModalHostProps) {
  const stack = useModalStore((s) => s.stack);

  return (
    <>
      {stack.map((instance) => (
        <ModalSlot key={instance.key} instance={instance} registry={registry} />
      ))}
    </>
  );
}

function ModalSlot({
  instance,
  registry,
}: {
  instance: ModalInstance;
  registry: ModalRegistryMap;
}) {
  const closeModal = useModalStore((s) => s.closeModal);
  const close = React.useCallback(
    () => closeModal(instance.key),
    [closeModal, instance.key],
  );

  // Validate the payload and resolve the component for this instance.
  const result = renderModal(instance, registry, close);
  if (!result.ok) {
    if (import.meta.env.DEV) {
      console.error(`[ModalHost] ${result.reason}`, result.detail);
    }
    // Close the invalid instance on the next tick (can't setState during render).
    queueMicrotask(close);
    return null;
  }

  return (
    <ModalControlsProvider value={{ open: true, close }}>
      <ModalErrorBoundary resetKey={instance.key} onError={close}>
        {result.node}
      </ModalErrorBoundary>
    </ModalControlsProvider>
  );
}

type RenderResult =
  | { ok: true; node: React.ReactNode }
  | { ok: false; reason: string; detail?: unknown };

/** Payload-erased view of a modal component, used only at the dispatch point. */
type ErasedModalComponent = React.ComponentType<{
  payload: unknown;
  close: () => void;
  open: boolean;
}>;

/**
 * Look up the definition for an instance, validate its payload, and produce the
 * element. The registry is keyed by a union id, so TS can't statically prove
 * `registry[id].component` accepts `instance.payload` — but they ARE correlated
 * at runtime by `id`, and the payload is schema-validated just above. The one
 * erased dispatch (`ErasedModalComponent`) is the single, contained bridge;
 * the public surface (`openModal`, the registry map, `ModalRenderProps`,
 * `defineModal`) stays fully typed, so nothing leaks to callers.
 */
function renderModal(
  instance: ModalInstance,
  registry: ModalRegistryMap,
  close: () => void,
): RenderResult {
  const definition = registry[instance.id];
  if (!definition) {
    return { ok: false, reason: `No modal registered for id "${instance.id}".` };
  }

  let payload: unknown = instance.payload;
  if (definition.schema) {
    const parsed = definition.schema.safeParse(instance.payload);
    if (!parsed.success) {
      return {
        ok: false,
        reason: `Invalid payload for modal "${instance.id}".`,
        detail: parsed.error.issues,
      };
    }
    payload = parsed.data;
  }

  const Component = definition.component as ErasedModalComponent;
  return { ok: true, node: <Component payload={payload} close={close} open /> };
}
